import type { DialogProps } from "@material-ui/core/Dialog";
import type {
  Transaction,
  TransactionFormError,
  TransactionFormModel,
} from "src/interfaces";
import type { Dispatch, SetStateAction } from "react";
import type { ForwardedRef } from "react";
import type {
  MappedOption,
  SplitRowInput,
} from "src/apiService/modules/transactions";

export interface SplitSaveOption {
  split_group_id?: string;
  rows: SplitRowInput[];
}

export interface EditTransactionFormProps {
  data: TransactionFormModel | undefined;
  loading: boolean;
  onSave: (
    transaction: Transaction,
    opts?: { next?: boolean; mapped?: MappedOption; split?: SplitSaveOption },
    newImageFile?: File
  ) => void;
  onDelete: Function;
  noCopyExpenseButton?: boolean;
  onMaxWidthChange: (width: DialogProps["maxWidth"]) => void;
}

export interface EditDialogProps {
  data?: TransactionFormModel | undefined;
  open: boolean;
  onClose: (hasChanged?: boolean) => void;
  onDelete: Function;
  loading?: boolean;
  noCopyExpenseButton?: boolean;
}

export type EditDialogContentWrapperProps = Omit<EditDialogProps, "open"> & {
  onMaxWidthChange: (width: DialogProps["maxWidth"]) => void;
};

export const initFormValues: Partial<Transaction> = {
  id: "",
  account: "",
  description: "",
  integration_name: "",
};

export interface FormProps {
  errors: TransactionFormError;
  values: Partial<Transaction>;
  loading: boolean;
  handleChange: (
    prop: keyof Transaction,
    numeric?: true
  ) => (event: { target: { value?: any } }) => void;
  setValues: Dispatch<SetStateAction<Partial<Transaction>>>;
  dateRef: ForwardedRef<HTMLDivElement>;
  /**
   * Whether this form should render its own Date field. Defaults to true.
   * Set to false when the parent renders the Date field itself (e.g. the
   * inventory layout lifts the Date field higher in the DOM so the tab order
   * flows account → date → vendor → … instead of following the CSS grid order).
   */
  showDate?: boolean;
  hideAmount?: boolean;
  hideDescription?: boolean;
}

export interface InventoryFormProps {
  errors: TransactionFormError;
  values: Partial<Transaction>;
  loading: boolean;
  setValues: Dispatch<SetStateAction<Partial<Transaction>>>;
}

export interface BalanceTransferCard {
  bank_account_id?: string;
  integration_name?: string;
  date?: Date;
  description?: string;
  /** Set when this card is prefilled from a matched transaction (edit: data.mapped, or search result). */
  id?: string;
  /** True when this card represents a matched transaction. Cleared when the user breaks the match. */
  match?: boolean;
}

/** Single state for the balance transfer form: root card (first edited), mapped card (other leg), and layout. */
export interface BalanceTransferState {
  root: BalanceTransferCard;
  mapped: BalanceTransferCard;
  /** True when the root card is shown on the left (From side). */
  rootOnLeft: boolean;
}
